import { emit } from './emit'
import { safeSessionGet, safeSessionRemove, safeSessionSet } from './sessionStore'
import { newUuid } from './uuid'

/**
 * Generic latency tracker: bracket a flow with start/stop and emit a
 * `latency_measured` event for the duration, or `latency_error` if it
 * fails. Steps in flight emit `latency_heartbeat` every 5s so the
 * warehouse can detect abandonment (no stop, no error).
 *
 * One step at a time per name. Calling `startLatencyTimer` while a step
 * with the same name is in flight resets it — this handles the
 * back-navigation retry pattern (user goes back, repeats the action).
 */

const STORAGE_PREFIX = 'latency_'
const TRACE_PREFIX = 'latency_trace_'
const HEARTBEAT_INTERVAL_MS = 5_000
const MAX_HEARTBEAT_DURATION_MS = 120_000

const activeHeartbeats = new Map<string, ReturnType<typeof setInterval>>()

function clearHeartbeat(step: string): void {
    const intervalId = activeHeartbeats.get(step)
    if (intervalId != null) {
        clearInterval(intervalId)
        activeHeartbeats.delete(step)
    }
}

/**
 * Writes Date.now() (or a custom startTime) to sessionStorage for the given step.
 * Always overwrites — if called again before stop(), the timer resets (handles back-navigation retries).
 *
 * Fires a `latency_start` event and begins heartbeat events every 5s for abandonment detection.
 *
 * Pass `startTime` to backdate the timer when the name isn't known until after an async call.
 */
export function startLatencyTimer(step: string, startTime?: number): void {
    const now = startTime ?? Date.now()
    const traceId = newUuid()
    safeSessionSet(`${STORAGE_PREFIX}${step}`, String(now))
    safeSessionSet(`${TRACE_PREFIX}${step}`, traceId)

    clearHeartbeat(step)

    emit('latency_start', { step, trace_id: traceId })

    try {
        const intervalId = setInterval(() => {
            try {
                const elapsed = Math.round(Date.now() - now)
                if (elapsed > MAX_HEARTBEAT_DURATION_MS || !safeSessionGet(`${STORAGE_PREFIX}${step}`)) {
                    clearHeartbeat(step)
                    return
                }
                emit('latency_heartbeat', { step, trace_id: traceId, elapsed_ms: elapsed })
            } catch {
                clearHeartbeat(step)
            }
        }, HEARTBEAT_INTERVAL_MS)

        activeHeartbeats.set(step, intervalId)
    } catch {
        // setInterval can fail in restrictive sandboxes; tracking still works without heartbeats.
    }
}

/**
 * Reads the stored start time, computes duration, fires a `latency_measured` event, and removes the key.
 * No-op if the key is missing (already stopped, never started, or user abandoned).
 */
export function stopLatencyTimer(step: string, metadata?: Record<string, unknown>): void {
    clearHeartbeat(step)

    const stored = safeSessionGet(`${STORAGE_PREFIX}${step}`)
    if (!stored) return

    const traceId = safeSessionGet(`${TRACE_PREFIX}${step}`)
    const durationMs = Math.round(Date.now() - Number(stored))
    safeSessionRemove(`${STORAGE_PREFIX}${step}`)
    safeSessionRemove(`${TRACE_PREFIX}${step}`)

    emit('latency_measured', {
        ...(metadata ?? {}),
        step,
        duration_ms: durationMs,
        trace_id: traceId,
    })
}

/**
 * Fires a `latency_error` event with the elapsed duration and error details, then removes the key.
 * No-op if the key is missing.
 */
export function errorLatencyTimer(
    step: string,
    errorType: string,
    errorMessage?: string,
    metadata?: Record<string, unknown>,
): void {
    clearHeartbeat(step)

    const stored = safeSessionGet(`${STORAGE_PREFIX}${step}`)
    if (!stored) return

    const traceId = safeSessionGet(`${TRACE_PREFIX}${step}`)
    const durationMs = Math.round(Date.now() - Number(stored))
    safeSessionRemove(`${STORAGE_PREFIX}${step}`)
    safeSessionRemove(`${TRACE_PREFIX}${step}`)

    emit('latency_error', {
        ...(metadata ?? {}),
        step,
        duration_ms: durationMs,
        error_type: errorType,
        error_message: errorMessage?.substring(0, 200),
        trace_id: traceId,
    })
}
